/**
 * Origine du backend (sans `/api`) pour résoudre les chemins `/uploads/...`
 * alors que le frontend tourne sur un autre port / domaine.
 */
function isRelativeApiBase(): boolean {
  const raw = (process.env.REACT_APP_API_URL || '').trim();
  return raw.startsWith('/') && !raw.startsWith('//');
}

export function getBackendOrigin(): string {
  const raw = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').trim();
  if (isRelativeApiBase()) {
    return typeof window !== 'undefined' ? window.location.origin : '';
  }

  const withoutApi = raw.replace(/\/api\/?$/i, '').replace(/\/$/, '');
  if (!withoutApi) {
    return typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000';
  }
  return withoutApi;
}

/** Normalise un asset uploadé vers un chemin portable `/uploads/public/...`. */
export function toPortableUploadedAssetPath(value: string): string {
  const normalized = value.trim().replace(/\\/g, '/');
  if (!normalized) {
    return '';
  }

  const directMatch = normalized.match(/\/uploads\/public\/[^?#]+/i);
  if (directMatch?.[0]) {
    return directMatch[0];
  }

  if (/^uploads\/public\//i.test(normalized)) {
    return `/${normalized}`;
  }

  if (!/^https?:\/\//i.test(normalized)) {
    return normalized;
  }

  try {
    const parsed = new URL(normalized);
    const pathMatch = parsed.pathname.match(/\/uploads\/public\/[^?#]+/i);
    if (pathMatch?.[0]) {
      return pathMatch[0];
    }
    return normalized;
  } catch {
    return normalized;
  }
}

/** Backends pour lesquels le proxy CRA (package.json "proxy") renvoie vers l'API en dev — URLs relatives = même origine, plus de blocage CORP sur les <img>. */
const DEV_UPLOADS_RELATIVE_ORIGINS = new Set(['http://localhost:3000', 'http://127.0.0.1:3000']);

function shouldUseRelativeUploadsPath(): boolean {
  if (process.env.NODE_ENV !== 'development') {
    return false;
  }
  if (typeof window === 'undefined') {
    return false;
  }
  if (!DEV_UPLOADS_RELATIVE_ORIGINS.has(getBackendOrigin())) {
    return false;
  }
  // CRA proxy (/uploads → backend) ne fonctionne que si le serveur dev peut joindre l'API.
  // Frontend :3001 + backend :3000 (Docker ou local) = origines différentes → URL absolue vers :3000.
  try {
    return window.location.origin === getBackendOrigin();
  } catch {
    return false;
  }
}

function resolveUploadsPath(pathname: string, search: string): string {
  if (shouldUseRelativeUploadsPath() || isRelativeApiBase()) {
    return `${pathname}${search}`;
  }
  const origin = getBackendOrigin();
  return origin ? `${origin}${pathname}${search}` : `${pathname}${search}`;
}

/**
 * Toute URL dont le chemin commence par `/uploads/` est recollée sur REACT_APP_API_URL
 * (corrige chemins relatifs, hôte faux en base après upload, 127.0.0.1 vs localhost, etc.).
 */
export function resolveBrandingLogoUrl(logoUrl: string | null | undefined): string | undefined {
  const s = typeof logoUrl === 'string' ? logoUrl.trim() : '';
  if (!s) {
    return undefined;
  }

  let pathname = '';
  let search = '';

  try {
    if (/^https?:\/\//i.test(s)) {
      const u = new URL(s);
      pathname = u.pathname;
      search = u.search;
    } else if (s.startsWith('//')) {
      const u = new URL(`https:${s}`);
      pathname = u.pathname;
      search = u.search;
    } else {
      const [pathPart, ...queryParts] = s.split('?');
      pathname = pathPart.startsWith('/') ? pathPart : `/${pathPart}`;
      search = queryParts.length > 0 ? `?${queryParts.join('?')}` : '';
    }
  } catch {
    return undefined;
  }

  if (pathname.startsWith('/uploads/')) {
    return resolveUploadsPath(pathname, search);
  }

  if (/^https?:\/\//i.test(s)) {
    return s;
  }
  if (s.startsWith('//')) {
    if (typeof window !== 'undefined') {
      return `${window.location.protocol}${s}`;
    }
    return `https:${s}`;
  }
  const path = s.startsWith('/') ? s : `/${s}`;
  const origin = getBackendOrigin();
  return origin ? `${origin}${path}` : path;
}
